/**
 * Google Sign In Controller
 *
 * Uses the Google Identity Services OAuth 2.0 authorization-code flow
 * (`google.accounts.oauth2.initCodeClient`) in popup mode. Clicking opens
 * Google's own sign-in window, which lets the visitor authenticate even with no
 * existing Google session, and hands back an authorization code. We POST that
 * code to the backend, which exchanges it for an ID token server-side using the
 * OAuth client secret.
 *
 * This deliberately is NOT `google.accounts.id.prompt()` (One Tap). One Tap can
 * only surface an already-signed-in Google session, so it silently renders
 * nothing for signed-out visitors, and Google applies a dismissal cooldown that
 * suppresses it for signed-in ones. It also cannot be driven from a custom
 * button. The code flow has none of those limits and keeps our own button.
 *
 * Usage:
 * <div data-controller="google-signin"
 *      data-google-signin-client-id-value="your-google-client-id"
 *      data-google-signin-callback-url-value="/auth/social/callback/">
 *   <button data-action="click->google-signin#signIn">
 *     Continue with Google
 *   </button>
 * </div>
 */

import ApplicationController from './application_controller';

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initCodeClient: (config: GoogleCodeClientConfig) => GoogleCodeClient;
        };
      };
    };
  }
}

interface GoogleCodeClientConfig {
  client_id: string;
  scope: string;
  ux_mode?: 'popup' | 'redirect';
  callback: (response: GoogleCodeResponse) => void;
  error_callback?: (error: GoogleOAuthError) => void;
}

interface GoogleCodeClient {
  requestCode: () => void;
}

interface GoogleCodeResponse {
  code?: string;
  scope?: string;
  error?: string;
  error_description?: string;
}

interface GoogleOAuthError {
  type: string;
  message?: string;
}

// openid is what makes the exchange return an id_token; without it the backend
// gets an access token it has no use for.
const OAUTH_SCOPE = 'openid email profile';

export default class extends ApplicationController {
  static values = {
    clientId: String,
    callbackUrl: { type: String, default: '/auth/social/callback/' },
    nextUrl: { type: String, default: '' },
  };

  declare clientIdValue: string;
  declare callbackUrlValue: string;
  declare nextUrlValue: string;

  private sdkLoaded: boolean = false;
  private sdkLoading: boolean = false;
  private codeClient: GoogleCodeClient | null = null;
  private requestPending: boolean = false;

  connect(): void {
    if (!this.clientIdValue) {
      console.warn('Google Sign In: Missing client ID');
      return;
    }
    this.loadGoogleSDK();
  }

  /**
   * Load the Google Identity Services SDK
   */
  private loadGoogleSDK(): void {
    if (this.sdkLoaded || this.sdkLoading) return;

    // Check if SDK is already loaded
    if (window.google?.accounts?.oauth2) {
      this.sdkLoaded = true;
      this.initializeGoogle();
      return;
    }

    this.sdkLoading = true;

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.sdkLoaded = true;
      this.sdkLoading = false;
      this.initializeGoogle();
    };
    script.onerror = () => {
      this.sdkLoading = false;
      console.error('Google Sign In: Failed to load SDK');
      this.reportFailure('sdk_load_failed');
    };

    document.head.appendChild(script);
  }

  /**
   * Build the authorization-code client
   */
  private initializeGoogle(): void {
    if (!window.google?.accounts?.oauth2) {
      console.error('Google Sign In: SDK not available');
      return;
    }

    this.codeClient = window.google.accounts.oauth2.initCodeClient({
      client_id: this.clientIdValue,
      scope: OAUTH_SCOPE,
      ux_mode: 'popup',
      callback: this.handleCodeResponse.bind(this),
      error_callback: this.handleOAuthError.bind(this),
    });
  }

  /**
   * Trigger Google Sign In flow
   */
  signIn(event?: Event): void {
    if (event) event.preventDefault();

    if (!this.codeClient) {
      console.error('Google Sign In: client not ready');
      this.reportFailure('not_ready');
      return;
    }

    // A second popup while one is already open just gets blocked by the
    // browser, and leaves the user staring at a dead button.
    if (this.requestPending) return;

    this.requestPending = true;
    this.codeClient.requestCode();
  }

  /**
   * Handle the authorization code response from Google
   */
  private handleCodeResponse(response: GoogleCodeResponse): void {
    this.requestPending = false;

    if (response.error) {
      console.error('Google Sign In:', response.error, response.error_description);
      this.reportFailure(response.error);
      return;
    }

    if (!response.code) {
      console.error('Google Sign In: No authorization code received');
      this.reportFailure('no_code');
      return;
    }

    this.submitToBackend(response.code);
  }

  /**
   * Handle popup-level failures (blocked, or closed before completing)
   */
  private handleOAuthError(error: GoogleOAuthError): void {
    this.requestPending = false;

    if (error.type === 'popup_closed') {
      // Deliberate cancellation - nothing to report.
      return;
    }

    console.error('Google Sign In popup error:', error.type, error.message);
    this.reportFailure(error.type);
  }

  /**
   * Let the page react to a failed sign-in attempt. Nothing listens today, but
   * this is the hook for showing a message rather than failing silently, which
   * is what the previous One Tap implementation did.
   */
  private reportFailure(reason: string): void {
    this.dispatch('error', { detail: { reason } });
  }

  /**
   * Submit the authorization code to the backend for exchange and validation
   */
  private submitToBackend(code: string): void {
    // Create a form and submit it
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = this.callbackUrlValue;
    form.style.display = 'none';

    const fields: Record<string, string> = {
      provider: 'google',
      code,
    };

    // Add CSRF token
    const csrfToken = this.getCSRFToken();
    if (csrfToken) {
      fields.csrfmiddlewaretoken = csrfToken;
    }

    // Add next URL if present
    if (this.nextUrlValue) {
      fields.next = this.nextUrlValue;
    }

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  /**
   * Get CSRF token from cookies or meta tag
   */
  private getCSRFToken(): string | null {
    // Try meta tag first
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    if (metaTag) {
      return metaTag.getAttribute('content');
    }

    // Fall back to cookie
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken') {
        return value;
      }
    }

    return null;
  }
}
